import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { ProjectContactDirectory } from 'src/app/_model/project-contact-directory';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';

import { ProjectContactDirectoryService } from '../project-contact-directory.service';
import { ProjectContactDirectoryDaoService } from '../../dao/project-contact-directory-dao.service';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads the project contact directory
 */
@Injectable()
export class ProjectContactDirectoryLogicService implements ProjectContactDirectoryService {
  public projectContactDirectory$ = new BehaviorSubject<ProjectContactDirectory[]>([]);
  public hasSyncedRemoteEntries = false;
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private projectContactDirectoryDaoService: ProjectContactDirectoryDaoService,
    private authenticationService: AuthenticationService,
    private servicejobsService: ServicejobsService
  ) {
    this.initialize();
  }

  // Resetable
  public async reset() {
    // Unlike clear(), reset() also wipes locally created (not-yet-synced) entries.
    // What's left afterwards says nothing about the backend anymore either.
    this.hasSyncedRemoteEntries = false;
    await this.projectContactDirectoryDaoService.reset();
    const remaining = await this.projectContactDirectoryDaoService.getProjectContactDirectory();
    this.projectContactDirectory$.next(remaining);
  }

  public async clear() {
    // Only truncates the synced table (see DAO) - reload so locally created
    // entries that survive the clear stay visible in the BehaviorSubject.
    await this.projectContactDirectoryDaoService.clear();
    const remaining = await this.projectContactDirectoryDaoService.getProjectContactDirectory();
    this.projectContactDirectory$.next(remaining);
  }

  /**
   * Creates a new, purely local ProjectContactDirectory entry (not sent to the backend).
   */
  public async createProjectContactDirectory(entry: ProjectContactDirectory): Promise<ProjectContactDirectory> {
    const created = await this.projectContactDirectoryDaoService.createProjectContactDirectory(entry);
    if (created) {
      this.projectContactDirectory$.next([...this.projectContactDirectory$.getValue(), created]);
    }
    return created;
  }

  /**
   * Removes a single entry - a locally created one for good, a synced one only
   * from this device's copy (see the DAO). Called from the form postprocessor
   * when the user removed the entry, so the list on screen matches the removal
   * that just went to the backend instead of waiting for the next sync.
   */
  public async deleteProjectContactDirectory(entry: ProjectContactDirectory): Promise<void> {
    if (entry.isLocal()) {
      await this.projectContactDirectoryDaoService.deleteLocalProjectContactDirectory(entry.id);
    } else {
      await this.projectContactDirectoryDaoService.deleteSyncedProjectContactDirectory(entry.id);
    }
    const remaining = await this.projectContactDirectoryDaoService.getProjectContactDirectory();
    this.projectContactDirectory$.next(remaining);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteProjectContactDirectory(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {
    await this.reconcileLocalEntries();
  }

  /**
   * Locally created entries have no backend id yet, so they can't be matched to
   * a synced entry by id. Once the backend has created the real entry (e.g. via
   * a form submission's postprocessor), it arrives as a separate synced entry -
   * find the one that stands for the same contact and drop the local duplicate.
   *
   * A synced entry is only ever claimed by one local one, so two contacts the
   * app created in the same function group can't both be resolved against the
   * same arrival.
   */
  private async reconcileLocalEntries(): Promise<void> {
    const all = this.projectContactDirectory$.getValue();
    const localEntries = all.filter((entry) => entry.isLocal());
    if (localEntries.length === 0) {
      return;
    }
    const remoteEntries = all.filter((entry) => !entry.isLocal());
    const claimed = new Set<ProjectContactDirectory>();

    let didDelete = false;
    for (const local of localEntries) {
      const match = remoteEntries.find(
        (remote) => !claimed.has(remote) && this.isSameContact(local, remote),
      );
      if (match) {
        claimed.add(match);
        this.logger.log(
          this.constructor.name,
          'Found a remote project contact directory entry for local entry, removing local duplicate',
          local.servicejobid,
          local.functiongroupid,
          local.personlabel,
        );
        await this.projectContactDirectoryDaoService.deleteLocalProjectContactDirectory(local.id);
        didDelete = true;
      }
    }

    if (didDelete) {
      const remaining = await this.projectContactDirectoryDaoService.getProjectContactDirectory();
      this.projectContactDirectory$.next(remaining);
    }
  }

  /**
   * Whether a synced entry is the backend's own copy of a locally created one.
   * Servicejob and function group are only the frame: a function group holds as
   * many contacts as the job needs (two devices adding a contact to the same
   * group is the normal case), so what decides is the contact itself.
   *
   * For an internal employee, and for a branch/person that was already synced
   * when the entry was created, the ids say it. For a contact this app created
   * itself they can't: its branch and person carry app-local ids until the
   * backend has built its own, so the person's name is the only thing both
   * sides share - and it is exactly what the backend built its Person from.
   */
  private isSameContact(local: ProjectContactDirectory, remote: ProjectContactDirectory): boolean {
    if (
      remote.servicejobid !== local.servicejobid ||
      remote.functiongroupid !== local.functiongroupid
    ) {
      return false;
    }

    if (local.employeeid != undefined || remote.employeeid != undefined) {
      return local.employeeid === remote.employeeid;
    }

    if (local.branchid != undefined && local.branchid === remote.branchid) {
      // Same branch on both sides - then the person has to line up as well, by
      // id when the person was synced too, by name when the app created it.
      return (
        (local.personid ?? null) === (remote.personid ?? null) ||
        ProjectContactDirectoryLogicService.isSameLabel(local.personlabel, remote.personlabel)
      );
    }

    return ProjectContactDirectoryLogicService.isSameLabel(local.personlabel, remote.personlabel);
  }

  private static isSameLabel(a: string, b: string): boolean {
    return (
      !!a && !!b && a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase()
    );
  }

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalProjectContactDirectory();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  async initializeCompleted(): Promise<void> {}

  // Private Functions
  private async loadLocalProjectContactDirectory(): Promise<void> {
    try {
      this.logger.log(this.constructor.name, '- Load local project contact directory');
      const entries = await this.projectContactDirectoryDaoService.getProjectContactDirectory();
      this.projectContactDirectory$.next(entries);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local project contact directory: ',
        error?.message
      );
      throw error;
    }
  }

  private async loadRemoteProjectContactDirectory(apiLevel: APILevel, syncKey: string): Promise<void> {
    if (!this.authenticationService.authenticationState.getValue()) {
      return;
    }

    if (apiLevel < APILevel.PROJECT_CONTACT_DIRECTORY) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to sync project contact directory'
      );
      return;
    }

    this.logger.log(this.constructor.name, '- Load new project contact directory from IMS');

    try {
      const sjobList = this.servicejobsService.servicejobs$
        .getValue()
        .map((sjob) => {
          return sjob.getImsId();
        })
        .join(',');

      const projectContactDirectoryData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncprojectcontactdirectory',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            sjoblist: sjobList,
            synckey: syncKey,
          },
        })
      );

      const entries: ProjectContactDirectory[] = projectContactDirectoryData.map((dto: any) =>
        ProjectContactDirectory.fromDto(dto)
      );

      this.logger.log(
        this.constructor.name,
        'Finished loading project contact directory (' + entries.length + ' Total)',
        sjobList,
        entries
      );

      await this.projectContactDirectoryDaoService.saveProjectContactDirectory(entries);
      // From here on, an entry the backend didn't send is an entry the backend
      // doesn't have - see ProjectContactDirectoryFormField.isDeletedInBackend().
      this.hasSyncedRemoteEntries = true;
      // Reload the full merged list (synced + local) so locally created entries stay visible.
      const merged = await this.projectContactDirectoryDaoService.getProjectContactDirectory();
      this.projectContactDirectory$.next(merged);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error downloading project contact directory: ',
        error?.message
      );
      throw error;
    }
  }
}
